import type { Schedule, UEResult } from '@habitos/engine';
import type { TFn } from '../i18n/core';
import type { Messages } from '../i18n/es';
import type { HabitRec } from '../domain/model';

/**
 * Texto de la unidad. Tanto las predefinidas como las propias del usuario usan
 * "singular|plural" (FR-HAB-15); sin `count` se devuelve el plural.
 */
export function unitLabel(m: Messages, unit: string | null | undefined, count?: number): string {
  if (!unit) return '';
  const [one, other = one] = ((m.units as Record<string, string>)[unit] ?? unit).split('|') as [string, string?];
  return count === 1 ? one : other;
}

export function quotaMode(s: Schedule | undefined): 'times' | 'amount' | null {
  return s && (s.type === 'weekly_quota' || s.type === 'monthly_quota') ? s.mode : null;
}

export function progressText(t: TFn, m: Messages, h: HabitRec, ue: UEResult): string {
  const v = ue.version;
  if (h.kind === 'quit') return t('habit.limitUsed', { value: ue.value, target: ue.required });
  if (quotaMode(v.schedule) === 'times') return t('habit.times', { value: ue.value, target: ue.required });
  if (v.goalType === 'binary') return t(`habit.status.${ue.status}`);
  if (ue.average) {
    // Con media móvil (FR-HAB-16) lo útil es cuánto falta hoy y cómo va la media.
    const avg = { avg: ue.average.value, target: v.target };
    return ue.required > 0
      ? t('habit.averageNeed', { value: ue.value, need: ue.required, unit: unitLabel(m, v.unit, ue.required), ...avg })
      : t('habit.averageMet', { value: ue.value, unit: unitLabel(m, v.unit, ue.value), ...avg });
  }
  const unit = unitLabel(m, v.unit, ue.required);
  return unit ? t('habit.progressUnit', { value: ue.value, target: ue.required, unit }) : t('habit.progress', { value: ue.value, target: ue.required });
}

export function streakText(t: TFn, count: number, unit: 'day' | 'week' | 'month'): string {
  return t(unit === 'day' ? 'habit.streakDays' : unit === 'week' ? 'habit.streakWeeks' : 'habit.streakMonths', { count });
}

export function minutesToHHMM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

export function hhmmToMinutes(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function errorMessage(t: TFn, err: unknown): string {
  if (err instanceof Error) {
    const known = ['future_date', 'before_start', 'not_scheduled', 'same_day', 'not_movable', 'source_not_scheduled', 'target_out_of_range', 'target_scheduled'];
    if (known.includes(err.message)) return t(`errors.${err.message}`);
  }
  return t('errors.save');
}
